import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@mui/material';
import { Stack } from '@mui/system';

import CreateCustomerModal from '@/shared/components/dialogs/CreateCustomerModal';
import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import Details from '@/shared/components/panels/CostsDetails/Details';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import { AppPath } from '@/shared/constants/path';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import DialogName from '@/shared/types/dialogName.enum';

import CreateOrderConfirmationDialog from './components/CreateOrderConfirmationDialog';
import CustomerDetails from './components/CustomerDetails';
import DiscardFormConfirmationDialog from './components/DiscardFormConfirmationDialog';
import DiscountPanel from './components/DiscountPanel/DiscountPanel';
import useDiscountPanel from './components/DiscountPanel/hooks/useDiscountPanel';
import ProductsSearchPanel from './components/ProductsSearchPanel/ProductsSearchPanel';
import Title from './components/Title';
import useCustomerFormValidation from './hooks/useCustomerFormValidation';
import useCustomerOperations from './hooks/useCustomerOperations';
import useOrderCreateOperations from './hooks/useOrderCreateOperations';
import useSearchCustomer from './hooks/useSearchCustomer';
import useSelectedProductsList from './hooks/useSelectedProductsList';

const OrderCreate: FC = () => {
  const { t } = useTranslation(['orders', 'common']);

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const [shouldResetForm, setShouldResetForm] = useState(false);
  const [currency, setCurrency] = useState<'CAD' | 'USD'>('CAD');

  const discountPanelState = useDiscountPanel();

  const {
    createdCustomer,
    updatedCustomer,
    handleCreateCustomer,
    handleUpdateCustomer,
  } = useCustomerOperations({
    onUpdateDialogState,
    onUpdateNotificationBar,
    onUpdateProgressDialog,
  });

  const {
    searchCustomersList,
    selectedCustomer,
    searchCustomerValue,
    selectedCustomerSnapshot,
    isBillingSameAsDelivery,
    setIsBillingSameAsDelivery,
    setSearchCustomerValue,
    setSelectedCustomerId,
    setSelectedCustomer,
  } = useSearchCustomer({
    createdCustomer,
    updatedCustomer,
    shouldResetForm,
    setShouldResetForm,
  });

  const { formErrors, onValidate, validateAllFields } =
    useCustomerFormValidation({
      customer: selectedCustomer[0],
      selectedCustomerSnapshot: selectedCustomerSnapshot[0],
      isBillingSameAsDelivery,
      shouldResetForm,
    });

  const {
    selectedProducts,
    onAddProductToSelection,
    onUpdateSelectedProduct,
    onDeleteSelectedProduct,
  } = useSelectedProductsList({ reset: shouldResetForm });

  const { handleSaveOrder } = useOrderCreateOperations({
    isBillingSameAsDelivery,
    onUpdateDialogState,
    onUpdateProgressDialog,
    onUpdateNotificationBar,
    setShouldResetForm,
  });

  const calculateCosts = (
    field: 'orderTotal' | 'subTotal' | 'shippingPrice' | 'discount' | 'tax',
  ) => {
    /**
     * SubTotal
     */
    const subTotal = selectedProducts.reduce((acc, curr) => {
      const amount = (curr.product_qty as number) || 1;

      const priceInProduct = curr.product_prices?.find(
        (item) => item.currency_code === currency,
      );

      const value = priceInProduct
        ? parseFloat(priceInProduct.value) * amount
        : curr.value * amount;

      return acc + value;
    }, 0);

    /**
     * Tax
     */
    const tax = subTotal * 0.1;

    /**
     * Discount
     */
    const discountAmount =
      parseFloat(discountPanelState.discount) > 0 &&
      parseFloat(discountPanelState.discount) < 100
        ? subTotal * (parseFloat(discountPanelState.discount) / 100)
        : 0;

    /**
     * OrderTotal
     */
    const shippingPrice = 0;

    /**
     * OrderTotal
     */
    const orderTotal = subTotal + tax + shippingPrice - discountAmount;

    return {
      orderTotal: orderTotal.toFixed(2),
      subTotal: subTotal.toFixed(2),
      discount: discountAmount.toFixed(2),
      tax: tax.toFixed(2),
      shippingPrice: '0',
    }[field];
  };

  return (
    <div>
      <Title
        isDiscardButtonDisabled={
          !selectedProducts.length && !selectedCustomer.length
        }
        isSaveButtonDisabled={
          !selectedProducts.length || !selectedCustomer.length
        }
        onCurrencyChange={(value) => setCurrency(value as typeof currency)}
        onDiscard={() => {
          onUpdateDialogState(DialogName.DiscardChanges, { open: true });
        }}
        onSaveOrder={() => {
          onUpdateDialogState(DialogName.SaveOrder, {
            open: true,
            errors: [],
          });
        }}
      />

      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(350px, 1fr))',
          alignItems: 'start',
          gap: 2,
        }}
      >
        <div>
          <CustomerDetails
            searchCustomersList={searchCustomersList}
            selectedCustomer={selectedCustomer}
            searchCustomerValue={searchCustomerValue}
            selectedCustomerSnapshot={selectedCustomerSnapshot}
            isBillingSameAsDelivery={isBillingSameAsDelivery}
            customerAddressFormErrors={formErrors}
            onValidateCustomerAddressForm={onValidate}
            setIsBillingSameAsDelivery={setIsBillingSameAsDelivery}
            setSearchCustomerValue={setSearchCustomerValue}
            setSelectedCustomerId={setSelectedCustomerId}
            setSelectedCustomer={setSelectedCustomer}
            onEditCustomer={() => {
              onUpdateDialogState(DialogName.EditCustomer, { open: true });
            }}
            onCreateCustomer={() => {
              onUpdateDialogState(DialogName.CreateCustomer, { open: true });
            }}
          />
        </div>

        <Stack spacing={2}>
          <ProductsSearchPanel
            currency={currency}
            selectedProducts={selectedProducts}
            onAddProductToSelection={onAddProductToSelection}
            onUpdateSelectedProducts={onUpdateSelectedProduct}
            onRemoveSelectedProduct={onDeleteSelectedProduct}
          />
          {/* <CostsDetails
            products={selectedProducts}
            currency={currency}
            discount={{ setDiscountPercent }}
          /> */}

          <DiscountPanel discountPanelState={discountPanelState} />

          <Details
            defaultExpanded
            shippingPrice={calculateCosts('shippingPrice')}
            orderTotal={calculateCosts('orderTotal')}
            subTotal={calculateCosts('subTotal')}
            discount={calculateCosts('discount')}
            tax={calculateCosts('tax')}
            currency={currency}
          />
        </Stack>

        {dialog?.createCustomer?.open && (
          <CreateCustomerModal
            open={dialog.createCustomer.open}
            onClose={() => {
              onUpdateDialogState(DialogName.CreateCustomer, { open: false });
            }}
            proceed={(customer) => {
              void handleCreateCustomer(customer);
            }}
          />
        )}

        {dialog?.editCustomer?.open && (
          <CreateCustomerModal
            open={dialog.editCustomer.open}
            customer={selectedCustomer[0]}
            onClose={() => {
              onUpdateDialogState(DialogName.EditCustomer, { open: false });
            }}
            proceed={(customer, customerId) => {
              if (customerId) {
                void handleUpdateCustomer(customerId, customer);
              }
            }}
          />
        )}

        {dialog?.discardChanges?.open && (
          <DiscardFormConfirmationDialog
            onClose={() => {
              onUpdateDialogState(DialogName.DiscardChanges, { open: false });
            }}
            proceed={() => {
              setShouldResetForm(true);
              onUpdateDialogState(DialogName.DiscardChanges, { open: false });
            }}
          />
        )}

        {dialog?.saveOrder?.open && (
          <CreateOrderConfirmationDialog
            errors={dialog.saveOrder.errors}
            onClose={() => {
              onUpdateDialogState(DialogName.SaveOrder, {
                open: false,
                errors: [],
                loading: false,
              });
            }}
            proceed={() => {
              const formHasErrors = validateAllFields();

              if (formHasErrors) {
                onUpdateDialogState(DialogName.SaveOrder, {
                  open: true,
                  errors: [t('orders:form_errors_notification')],
                });
              } else {
                void handleSaveOrder({
                  selectedCustomer: selectedCustomer[0],
                  selectedProducts: selectedProducts,
                  currency,
                  discount_percent: discountPanelState.discount,
                });
              }
            }}
          />
        )}

        <WaveUINotificationBar
          message={notificationBar.message}
          severity={notificationBar.severity}
          open={notificationBar.open}
          close={() => onUpdateNotificationBar({ open: false })}
          renderMessage={(message) => {
            if (dialog?.saveOrder?.data) {
              return (
                <>
                  {message}
                  <Link
                    to={`${AppPath.Orders}/${dialog?.saveOrder?.data as string}`}
                  >
                    <Button
                      variant='contained'
                      size='small'
                      sx={{
                        ml: 2,
                      }}
                    >
                      {t('orders:viewOrder')}
                    </Button>
                  </Link>
                </>
              );
            }

            return message;
          }}
        />

        <WaveUIProgressDialog
          title={progressDialog?.title}
          progress={progressDialog?.progress || 0}
          open={progressDialog?.open || false}
          onClose={() => onUpdateProgressDialog({ open: false })}
        />
      </Stack>
    </div>
  );
};

export default OrderCreate;
